import type { FunctionalComponent } from 'preact';
import { useEffect } from 'preact/hooks';
import { route } from 'preact-router';
import { env } from '@/lib/env.ts';
import { finishLogin } from '@/lib/login-flow.ts';
import {
  exchangeOauth,
  postOauthResult,
  redeemLoginTicket,
  takeOauthReturn,
} from '@/lib/oauth.ts';
import { useAppDispatch } from '@/store/hooks.ts';
import type { PageProps } from '@/types/props.ts';

/**
 * 카카오 로그인 콜백 (`/oauth/kakao.callback`).
 *
 * 두 가지로 들어온다.
 *
 *  1) `?ticket=`  — **인앱 브라우저 로그인** (2026-09-17)
 *     카카오톡 안의 브라우저는 팝업이 진짜 창으로 열리지 않는다. 그래서 페이지 전체가
 *     카카오 -> 백엔드로 갔다가, 백엔드가 여기로 1회용 티켓을 들려 돌려보낸다.
 *     이 화면이 티켓을 로그인 결과로 바꾸고 **이 창에서 그대로** 로그인을 마친다.
 *     (팝업이 원래 창을 못 찾았을 때의 비상구도 같은 길로 온다)
 *
 *  2) `?code=`    — 팝업 안에서 열린 경우 (현재 기본 설정에서는 쓰지 않는다)
 *     카카오 개발자센터의 redirect_uri 를 이 화면으로 바꾸면 code 를 받아 백엔드에
 *     교환을 요청하고, 결과를 부모창에 넘긴 뒤 닫는다.
 */
const OauthKakao: FunctionalComponent<PageProps> = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // ── 1) 인앱 브라우저: 티켓으로 로그인을 마친다 ───────────────────────────
    const ticket = params.get('ticket');
    if (ticket) {
      /*
        ★ 주소에서 티켓을 **바로 지운다.** 한 번 쓰면 못 쓰는 값이라, 남겨 두면
          뒤로가기로 이 화면에 다시 왔을 때 "이미 사용한 로그인 정보" 가 뜬다.
          방문 기록에 남길 이유도 없다.
      */
      window.history.replaceState(null, '', '/oauth/kakao.callback');

      const returnTo = takeOauthReturn();

      void (async () => {
        try {
          const user = await redeemLoginTicket(ticket);
          await finishLogin(dispatch, user, returnTo);
        } catch (err) {
          // 막다른 화면에 두지 않는다. 로그인 화면으로 돌려보내 이유를 보여준다.
          const message = err instanceof Error ? err.message : '카카오 로그인에 실패했습니다.';
          route(
            `/login?oauth_error=${encodeURIComponent(message)}&r=${encodeURIComponent(returnTo)}`,
            true
          );
        }
      })();
      return;
    }

    // ── 2) 팝업: code 를 교환해 부모창에 넘긴다 ──────────────────────────────
    const code = params.get('code');
    if (!code) {
      postOauthResult({
        type: 'KAKAO_FAIL',
        payload: 'code 가 없습니다. 콜백 URL 설정을 확인하세요.',
      });
      window.close();
      return;
    }

    void (async () => {
      try {
        const user = await exchangeOauth(env.kakao.api, { code });
        postOauthResult({ type: 'KAKAO_SUCCESS', payload: user });
      } catch (err) {
        postOauthResult({
          type: 'KAKAO_FAIL',
          payload: err instanceof Error ? err.message : '카카오 로그인에 실패했습니다.',
        });
      } finally {
        window.close();
      }
    })();
  }, [dispatch]);

  return <div class="oauth-callback">로그인 중입니다.</div>;
};

export default OauthKakao;
